import { Share03, Users01 } from "@untitledui/icons"
import { useEffect, useRef, useState } from "react"

import { Avatar } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Link } from "@/components/ui/link"
import { adminUserPath } from "@/routes"
import type { BriefContributor } from "@/types/contents"

/**
 * ContributorsSection
 */
const ROW_HEIGHT_PX = 40

export function ContributorsSection({
  title,
  contributors,
}: {
  title: string
  contributors: BriefContributor[]
}) {
  const [expanded, setExpanded] = useState(false)
  const [overflowing, setOverflowing] = useState(false)
  const listRef = useRef<HTMLDivElement>(null)

  // scrollHeight ignores the max-height clip, so this detects overflow
  // whether collapsed or expanded, and re-runs whenever the list resizes.
  useEffect(() => {
    const el = listRef.current
    if (!el) return

    const measure = () => setOverflowing(el.scrollHeight > ROW_HEIGHT_PX + 1)

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [contributors])

  if (contributors.length === 0) return null

  return (
    <section aria-label={title} className="flex flex-col gap-3">
      <div className="bg-surface-grouped rounded-xl p-2">
        <div className="flex flex-row justify-between">
          <div className="flex">
            <h2 className="text-primary mb-3 text-sm font-semibold">{title}</h2>
          </div>
          <div className="flex">
            <ContributorNumber contributorNumber={contributors.length} />
          </div>
        </div>
        <div
          ref={listRef}
          className="overflow-hidden"
          style={expanded ? undefined : { maxHeight: ROW_HEIGHT_PX }}
        >
          <ul className="flex flex-wrap gap-4">
            {contributors.map((contributor) => (
              <li key={contributor.id}>
                <ContributorCard contributor={contributor} />
              </li>
            ))}
          </ul>
        </div>
        {overflowing && (
          <Button
            color="link-color"
            size="sm"
            className="mt-2"
            onClick={() => setExpanded((prev) => !prev)}
          >
            {expanded ? "See less" : "See more"}
          </Button>
        )}
      </div>
    </section>
  )
}

function ContributorCard({ contributor }: { contributor: BriefContributor }) {
  const content = (
    <>
      <Avatar
        name={contributor.display_name}
        avatarUrl={contributor.user?.avatar_url ?? null}
        size="sm"
      />
      <div>
        <p className="text-primary flex items-center gap-1 text-sm font-medium">
          {contributor.display_name}
          {contributor.user?.id && (
            <Share03 aria-hidden="true" className="size-3 shrink-0" />
          )}
        </p>
        <p className="text-quaternary text-sm capitalize">{contributor.role}</p>
      </div>
    </>
  )

  if (!contributor.user) {
    return <div className="flex items-center gap-2">{content}</div>
  }

  return (
    <Link
      href={adminUserPath(contributor.user.id)}
      target="_blank"
      className="flex items-center gap-2 hover:cursor-pointer"
    >
      {content}
    </Link>
  )
}

function ContributorNumber({
  contributorNumber,
}: {
  contributorNumber: number
}) {
  if (contributorNumber === 0) return null

  return (
    <div className="text-tertiary flex items-center gap-1 text-sm font-medium tabular-nums">
      <span>{contributorNumber}</span>
      <Users01 aria-hidden="true" className="text-fg-quaternary size-4" />
    </div>
  )
}
